import {BaseComponentWithContext} from '@mintdata/mint-kit/api/components/BaseComponentWithContext';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {DataSource, IList, IListItemState, ListModel} from '@mintdata/mint-kit/api/models/List';
import {
    IListItemData,
    ListItemRenderer,
    ListView,
    ListViewConstructor
} from '@mintdata/mint-kit/impl/components/list/ListView';
import {Popover} from '@mintdata/mint-kit/impl/components/popover/Popover';
import {
    PopupAnchor,
    PopupGroup,
    PopupSource,
    SourceAnchor
} from '@mintdata/mint-kit/impl/components/popover/PopupTypes';
import {ImgIcon} from '@mintdata/mint-kit/impl/properties/components/imgIcon/ImgIcon';
import {ImgIcons} from '@mintdata/mint-kit/impl/properties/components/imgIcon/ImgIcons';
import {SvgIcon} from '@mintdata/mint-kit/impl/properties/components/svgIcon/SvgIcon';
import {SvgIcons} from '@mintdata/mint-kit/impl/properties/components/svgIcon/SvgIcons';
import {DomUtils} from '@mintdata/mint-kit/impl/util/DomUtils';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {IStyles, Styles} from '@mintdata/mint-kit/impl/util/Styles';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import * as React from 'react';

const contextMenuStyles = require('@mintdata/mint-kit/impl/components/contextMenu/ContextMenu.less');

export type ContextMenuTarget = HTMLElement | IContextMenuItem;
export type ContextMenuItemHandler = (item:IContextMenuItem) => void;
export type ContextMenuDataSourceProvider<T extends IContextMenuItem> = (target:ContextMenuTarget) => DataSource<T>;
export type ContextMenuDataSource<T extends IContextMenuItem> = DataSource<T> | ContextMenuDataSourceProvider<T>;

export interface IContextMenuItem extends IListItemData
{
    icon?:ImgIcons | SvgIcons | JSX.Element;
    rightLabel?:string | JSX.Element;
    isDisabled?:boolean;
    isSelected?:boolean;
    isSeparator?:boolean;
    handler?:ContextMenuItemHandler;
    dataSource?:ContextMenuDataSource<IContextMenuItem> | JSX.Element;
}

export interface IIContextMenuItemState
{
    id:GUID;
    isDataSourcePopoverOpen:boolean;
    dataSource:JSX.Element;
}

export interface IContextMenuProps<T extends IContextMenuItem>
{
    className?:string;
    dataSource:ContextMenuDataSource<T>;
    toggleOn?:Array<ToggleOn>,
    togglersIds?:Array<string>;
    theme?:IStyles;
    itemRenderer?:ListItemRenderer<T>;
    sourceAnchor?:SourceAnchor;
    popupAnchor?:PopupAnchor;
    popupClassName?:string;
    contentClassName?:string;
    useMousePositionAsPopupSource?:boolean;
    onShow?:() => (void);
    onHide?:() => (void);
    isOpen?:boolean;
    popupSource?:PopupSource;
    popupGroup?:PopupGroup;
    clearParentOpenChangeTimeoutId?:() => void;
    closeSelfOnlyOnSelectItem?:boolean;
}

export enum ToggleOn
{
    CONTEXT_MENU,
    CLICK,
    HOVER,
}

export {SourceAnchor, PopupAnchor};

export interface IContextMenuState<T extends IContextMenuItem>
{
    isOpen:boolean;
    popupSource?:PopupSource;
    popupSourceRect?:ClientRect;
    listModel:IList<T>;
    itemWithJSXDataSourceStates:Map<GUID, IIContextMenuItemState>;
    styles:IStyles;
}

export class ContextMenu<T extends IContextMenuItem> extends BaseComponentWithContext<IContextMenuProps<T>, IContextMenuState<T>>
{
    private _contextMenuRef:HTMLElement;
    private _itemWithJSXDataSourceRefs:Map<GUID, HTMLElement> = new Map();
    private readonly _id:GUID = IdUtil.newGUID();

    private static readonly _hoverOpenCloseDelayMs:number = 150;

    private _openChangeTimeoutId:number = null;

    @bind
    private _clearParentOpenChangeTimeoutId():void
    {
        if( this._openChangeTimeoutId )
            window.clearTimeout(this._openChangeTimeoutId);
    }

    private _setOpenChangeTimeout(callback:() => void, timeout:number):void
    {
        if( this._openChangeTimeoutId )
            window.clearTimeout(this._openChangeTimeoutId);

        this._openChangeTimeoutId = window.setTimeout(callback, timeout);
    }

    public constructor(props:any)
    {
        super(props);

        this.state = {
            isOpen: props.isOpen !== undefined ? props.isOpen : false,
            listModel: null,
            styles: null,
            popupSource: props.popupSource !== undefined ? props.popupSource : null,
            popupSourceRect: null,
            itemWithJSXDataSourceStates: new Map()
        };
    }

    public componentWillMount():void
    {
        const {dataSource, theme} = this.props;
        this._initDataSource(dataSource);
        this._initStyles(theme);
    }

    public componentDidMount():void
    {
        this.componentDidUpdate();
    }

    public componentWillUnmount():void
    {
        if( this._openChangeTimeoutId )
            window.clearTimeout(this._openChangeTimeoutId);
    }

    public componentWillReceiveProps(newProps:IContextMenuProps<T>):void
    {
        const {dataSource, theme} = this.props;

        if( newProps.isOpen !== undefined )
            this.setState({isOpen: newProps.isOpen});

        if( newProps.popupSource !== undefined )
            this.setState({popupSource: newProps.popupSource});

        if( dataSource !== newProps.dataSource )
            this._initDataSource(newProps.dataSource);

        if( theme !== newProps.theme )
            this._initStyles(newProps.theme);
    }

    public componentDidUpdate():void
    {
        const {popupManager} = this.context;
        const {sourceAnchor, popupAnchor, useMousePositionAsPopupSource} = this.props;

        const popupSource = useMousePositionAsPopupSource
            ? this.state.popupSourceRect
            : this.state.popupSource || this._contextMenuRef;

        const fixedPositionHandler:any = popupManager.fixedPosition(
            this._id,
            popupSource,
            sourceAnchor,
            popupAnchor
        );

        fixedPositionHandler();
    }

    @bind
    private _onElementRef(elementRef:HTMLElement)
    {
        this._contextMenuRef = elementRef;
    }

    private _getHeaderFooterTemplates()
    {
        const templates = {header: null as any, footer: null as any};

        const {children = []} = this.props;

        React.Children.forEach(children, (child:React.ReactElement<any>) =>
        {
            if( child.type === 'header' )
                templates.header = child;

            if( child.type === 'footer' )
                templates.footer = child;
        });

        return templates;
    }

    @bind
    private _onContextMenuItemSelect({_data: data}:any):void
    {
        const {popupManager} = this.context;

        if( data && !data.isDisabled && data.handler )
        {
            data.handler(data);
            // close menu with its submenus
            if( this.props.closeSelfOnlyOnSelectItem )
                popupManager.closeTopmostPopup();
            else
                popupManager.onNonPopupItemClicked();
        }
    }

    @bind
    private _onContextMenuItemMouseEnter(data:T, target:any):void
    {
        const {popupManager} = this.context;

        const toggler:HTMLElement = this._getToggler(target as HTMLElement);

        const popupElement = popupManager.findPopupFromElement(toggler);

        if( popupElement )
        {
            this._setOpenChangeTimeout(
                () => popupManager.onPopupItemClicked(popupElement),
                ContextMenu._hoverOpenCloseDelayMs
            );
        }

        // event.preventDefault();
        // event.stopPropagation();
    }

    @bind
    private _onContextMenuItemMouseLeave(data:T, target:any):void
    {
    }

    private _getToggler(target:HTMLElement):HTMLElement | null
    {
        const {togglersIds = []} = this.props;

        if( !togglersIds.length || togglersIds.indexOf(target.id) !== -1 )
            return target;

        let toggler = null;

        DomUtils.eachParentElement(target, (parentElement) =>
        {
            if( !parentElement )
                return false;

            if( togglersIds.indexOf(parentElement.id) !== -1 )
            {
                toggler = parentElement;
                return false;
            }
        });

        return toggler;
    }

    @bind
    private _onContextMenu(event:React.MouseEvent<HTMLDivElement>):void
    {
        const {toggleOn, isOpen} = this.props;

        if( isOpen !== undefined )
            return;

        if( !toggleOn || (toggleOn && toggleOn.indexOf(ToggleOn.CONTEXT_MENU) !== -1) )
        {
            const toggler:HTMLElement = this._getToggler(event.target as HTMLElement);

            const {dataSource} = this.props;

            if( !toggler )
                return;

            if( typeof dataSource === 'function' )
                this._initDataSource(dataSource(toggler));

            const togglerRect = this._getTogglerRect(toggler, event);

            this._openPopover(toggler, togglerRect);

            event.preventDefault();
            event.stopPropagation();
        }
    }

    @bind
    private _onClick(event:React.MouseEvent<HTMLDivElement>):void
    {
        const {toggleOn, isOpen} = this.props;

        if( isOpen !== undefined )
            return;

        if( toggleOn && toggleOn.indexOf(ToggleOn.CLICK) !== -1 )
        {
            const toggler:HTMLElement = this._getToggler(event.target as HTMLElement);
            const {dataSource} = this.props;
            const {isOpen} = this.state;

            if( !toggler )
                return;

            if( !isOpen )
            {
                if( typeof dataSource === 'function' )
                    this._initDataSource(dataSource(toggler));

                const togglerRect = this._getTogglerRect(toggler, event);
                this._openPopover(toggler, togglerRect);
            }
            else
                this._closePopover();

            event.preventDefault();
            event.stopPropagation();
        }
    }

    @bind
    private _onMouseEnter(event:React.MouseEvent<HTMLDivElement>):void
    {
        const {popupManager} = this.context;
        const {toggleOn, isOpen} = this.props;

        if( isOpen !== undefined )
            return;

        if( toggleOn && toggleOn.indexOf(ToggleOn.HOVER) !== -1 )
        {
            const toggler:HTMLElement = this._getToggler(event.target as HTMLElement);

            if( !toggler )
                return;

            const popupElement = popupManager.findPopupFromElement(toggler as HTMLElement);

            const {dataSource} = this.props;

            if( typeof dataSource === 'function' )
                this._initDataSource(dataSource(toggler));

            const togglerRect = this._getTogglerRect(toggler, event);

            this._setOpenChangeTimeout(
                () =>
                {
                    if( popupElement )
                        popupManager.onPopupItemClicked(popupElement);

                    this._openPopover(toggler, togglerRect);
                },
                ContextMenu._hoverOpenCloseDelayMs
            );

            event.preventDefault();
            event.stopPropagation();
        }
    }

    @bind
    private _onMouseLeave(event:React.MouseEvent<HTMLDivElement>):void
    {
        const {toggleOn, isOpen} = this.props;

        if( isOpen !== undefined )
            return;

        if( toggleOn && toggleOn.indexOf(ToggleOn.HOVER) !== -1 )
        {
            this._setOpenChangeTimeout(
                () => this._closePopover(),
                ContextMenu._hoverOpenCloseDelayMs
            );

            event.preventDefault();
            event.stopPropagation();
        }
    }

    @bind
    private _onPopoverMouseEnter(event:React.MouseEvent<HTMLElement>):void
    {
        const {toggleOn, isOpen} = this.props;

        if( isOpen !== undefined )
            return;

        if( toggleOn && toggleOn.indexOf(ToggleOn.HOVER) !== -1 )
        {
            if( this._openChangeTimeoutId )
                window.clearTimeout(this._openChangeTimeoutId);

            if( this.props.clearParentOpenChangeTimeoutId )
                this.props.clearParentOpenChangeTimeoutId();
        }
    }

    private _getTogglerRect(toggler:HTMLElement, event:React.MouseEvent<HTMLDivElement>):ClientRect
    {
        const {useMousePositionAsPopupSource} = this.props;

        let togglerRect = toggler.getBoundingClientRect();

        if( useMousePositionAsPopupSource )
        {
            togglerRect = this._positionToClientRect(event) as DOMRect;
        }

        return togglerRect;
    }

    private _positionToClientRect(event:React.MouseEvent<HTMLDivElement>):ClientRect
    {
        const left:number = event.clientX;
        const top:number = event.clientY;

        return {
            left,
            top,
            right: left,
            bottom: top,
            width: 0,
            height: 0
        };
    }

    private _initDataSource(dataSource:ContextMenuDataSource<T>):void
    {
        if( typeof dataSource === 'function' )
            dataSource = dataSource(this._contextMenuRef);

        const listModel:ListModel<T> = dataSource instanceof ListModel
            ? dataSource
            : new ListModel<T>(dataSource as Array<T>);

        this.setState({
            listModel
        });
    }

    private _initStyles(theme:IStyles):void
    {
        this.setState({
            styles: Styles.mergeStyles(contextMenuStyles, theme)
        });
    }

    private _openPopover(popupSource:PopupSource, popupSourceRect:ClientRect):void
    {
        this.setState({
            isOpen: true,
            popupSource,
            popupSourceRect
        });

        const onShowFn = this.props.onShow;
        onShowFn && onShowFn();
    }

    @bind
    private _closePopover():void
    {
        this.setState({isOpen: false});

        const onHideFn = this.props.onHide;
        onHideFn && onHideFn();
    }

    private _renderPopover()
    {
        const templates = this._getHeaderFooterTemplates();
        const {
            popupClassName,
            contentClassName,
            itemRenderer = this._renderItem,
            popupGroup
        } = this.props;
        const {listModel, styles} = this.state;
        const cx = classnames.bind(styles);

        if( !listModel || listModel.getItems().length <= 0 )
            return null;

        const TypedList:ListViewConstructor<T> = ListView as any;

        return (
            <Popover
                className={popupClassName}
                id={this._id}
                isModal={false}
                onDismissed={this._closePopover}
                group={popupGroup}
            >
                <div
                    className={cx('content', contentClassName)}
                    onMouseEnter={this._onPopoverMouseEnter}
                >
                    {templates.header}
                    <TypedList
                        dataSource={listModel}
                        itemRenderer={itemRenderer}
                        onItemSelect={this._onContextMenuItemSelect}
                        onItemHover={{
                            onItemMouseEnter: this._onContextMenuItemMouseEnter,
                            onItemMouseLeave: this._onContextMenuItemMouseLeave
                        }}
                        theme={styles}
                    />
                    {templates.footer}
                </div>
            </Popover>
        );
    }

    private _renderDataSourceJSXElements():JSX.Element[]
    {
        if( this.state.itemWithJSXDataSourceStates.size > 0 )
        {
            return Array.from(this.state.itemWithJSXDataSourceStates.values())
                .filter((itemState:IIContextMenuItemState) => itemState.isDataSourcePopoverOpen)
                .map((itemState:IIContextMenuItemState) =>
                {
                    return (
                        <Popover
                            key={itemState.id}
                            id={itemState.id}
                            isModal={false}
                            popupSource={this._itemWithJSXDataSourceRefs.get(itemState.id)}
                            popupAnchor={PopupAnchor.LeftTop}
                            sourceAnchor={SourceAnchor.RightTop}
                            onDismissed={this._getCloseDataSourcePopoverHandler(itemState.id)}
                            group={this.props.popupGroup}
                        >
                            <div
                                onMouseEnter={this._getOpenDataSourcePopoverHandler(itemState.id)}
                            >
                                {itemState.dataSource}
                            </div>
                        </Popover>
                    );
                });
        }

        return null;
    }

    @bind
    private _getOpenDataSourcePopoverHandler(itemId:GUID):(event:React.MouseEvent<HTMLDivElement>) => void
    {
        const {popupManager} = this.context;

        return (event:React.MouseEvent<HTMLDivElement>) =>
        {
            // const toggler:HTMLElement = this._getToggler(event.target as HTMLElement);
            const popupElement = popupManager.findPopupFromElement(event.target as HTMLElement);

            this._setOpenChangeTimeout(
                () =>
                {
                    if( popupElement )
                        popupManager.onPopupItemClicked(popupElement);

                    this.setState((prevState:IContextMenuState<T>) =>
                    {
                        const prevItemStates:Map<GUID, IIContextMenuItemState> = new Map(prevState.itemWithJSXDataSourceStates);
                        const prevItemState:IIContextMenuItemState = prevItemStates.get(itemId);

                        prevItemStates.set(itemId, {
                            ...prevItemState,
                            isDataSourcePopoverOpen: true
                        });

                        return {itemWithJSXDataSourceStates: prevItemStates};
                    });
                },
                ContextMenu._hoverOpenCloseDelayMs
            );
        };
    };

    @bind
    private _getCloseDataSourcePopoverHandler(itemId:GUID):() => void
    {
        return () =>
        {
            this._setOpenChangeTimeout(
                () =>
                {
                    this.setState((prevState:IContextMenuState<T>) =>
                    {
                        const prevItemStates:Map<GUID, IIContextMenuItemState> = new Map(prevState.itemWithJSXDataSourceStates);
                        const prevItemState:IIContextMenuItemState = prevItemStates.get(itemId);

                        prevItemStates.set(itemId, {
                            ...prevItemState,
                            isDataSourcePopoverOpen: false
                        });

                        return {itemWithJSXDataSourceStates: prevItemStates};
                    });
                },
                ContextMenu._hoverOpenCloseDelayMs
            );
        };
    };

    @bind
    private _renderItem(data:IContextMenuItem, state:IListItemState, styles:IStyles):JSX.Element
    {
        const {
            dataSource,
            isSeparator,
            label,
            rightLabel,
            icon,
            isSelected,
            isDisabled
        } = data;

        const cx = classnames.bind(styles);
        const className = cx('menuItem', {isSelected, isDisabled});

        if( isSeparator )
            return <div className={styles.separator} />;

        let iconElem:JSX.Element = null;

        if( isSelected )
            iconElem = <SvgIcon className={styles.svgIconElem} name={SvgIcons.check} />;
        else if( icon )
        {
            if( React.isValidElement(icon) )
                iconElem = icon;
            else if( Object.values(SvgIcons).includes(icon as SvgIcons) )
                iconElem = <SvgIcon className={styles.svgIconElem} name={icon as SvgIcons} />;
            else if( Object.values(ImgIcons).includes(icon as ImgIcons) )
                iconElem = <ImgIcon className={styles.imgIconElem} icon={(icon as ImgIcons)} />;
        }

        if( dataSource )
        {
            const TypedContextMenu:ContextMenuConstructor<any> = ContextMenu as any;
            const togglerId = IdUtil.newGUID();

            const arrowRightIcon = <SvgIcon className={styles.arrowRight} name={SvgIcons.arrowRight} />;

            if( isDisabled )
            {
                return (
                    <div className={className}>
                        <div className={cx('menuItemIcon')}>{iconElem}</div>
                        <span className={cx('menuItemLabel')}>{label}</span>
                        {rightLabel && <span className={cx('menuItemRightLabel')}>{rightLabel}</span>}
                        {arrowRightIcon}
                    </div>
                );
            }

            if( React.isValidElement(dataSource) )
            {
                return (
                    <div
                        className={className}
                        ref={this._getMenuItemMountHandler(state.id, dataSource)}
                        onMouseEnter={this._getOpenDataSourcePopoverHandler(state.id)}
                        onMouseLeave={this._getCloseDataSourcePopoverHandler(state.id)}
                    >
                        <div className={cx('menuItemIcon')}>{iconElem}</div>
                        <span className={cx('menuItemLabel')}>{label}</span>
                        {rightLabel && <span className={cx('menuItemRightLabel')}>{rightLabel}</span>}
                        {arrowRightIcon}
                    </div>
                );
            }
            else
            {
                return (
                    <TypedContextMenu
                        togglersIds={[togglerId]}
                        toggleOn={[ToggleOn.HOVER]}
                        dataSource={dataSource as ContextMenuDataSource<IContextMenuItem>}
                        popupAnchor={PopupAnchor.LeftTop}
                        sourceAnchor={SourceAnchor.RightTop}
                        popupGroup={this.props.popupGroup}
                        clearParentOpenChangeTimeoutId={this._clearParentOpenChangeTimeoutId}
                    >
                        <div id={togglerId} className={className}>
                            <div className={cx('menuItemIcon')}>{iconElem}</div>
                            <span className={cx('menuItemLabel')}>{label}</span>
                            {rightLabel && <span className={cx('menuItemRightLabel')}>{rightLabel}</span>}
                            {arrowRightIcon}
                        </div>
                    </TypedContextMenu>
                );
            }
        }

        return (
            <div className={className}>
                <div className={cx('menuItemIcon')}>{iconElem}</div>
                <div className={cx('menuItemLabel')}>{label}</div>
                {rightLabel && <span className={cx('menuItemRightLabel')}>{rightLabel}</span>}
            </div>
        );
    }

    private _getMenuItemMountHandler(itemId:GUID, dataSource:JSX.Element):(ref:HTMLElement | null) => void
    {
        return (ref:HTMLElement | null) =>
        {
            this._itemWithJSXDataSourceRefs.set(itemId, ref);

            if( this.state.itemWithJSXDataSourceStates.has(itemId) ) return;

            this.setState((prevState:IContextMenuState<T>) =>
            {
                const prevItemStates:Map<GUID, IIContextMenuItemState> = new Map(prevState.itemWithJSXDataSourceStates);

                prevItemStates.set(itemId, {
                    id: itemId,
                    isDataSourcePopoverOpen: false,
                    dataSource
                });

                return {itemWithJSXDataSourceStates: prevItemStates};
            });
        };
    }

    public render():JSX.Element
    {
        const {children, className} = this.props;
        const {isOpen, styles} = this.state;
        const cx = classnames.bind(styles);

        return (
            <div
                ref={this._onElementRef}
                className={cx('contextMenu', className)}
                onContextMenu={this._onContextMenu}
                onClickCapture={this._onClick}
                onMouseEnter={this._onMouseEnter}
                onMouseLeave={this._onMouseLeave}
            >
                {children}
                {isOpen && this._renderPopover()}
                {this._renderDataSourceJSXElements()}
            </div>
        );
    }
}

export type ContextMenuConstructor<T extends IContextMenuItem> = new() => ContextMenu<T>;
